'use client'

import { authSignOut } from '@/actions/auth/auth-sign-out'
import * as action from '@/actions/user/user-get-me'
import { UserModel } from '@/libs/api-client'
import { Bars3Icon } from '@heroicons/react/24/solid'
import { Link } from '@/i18n/navigation'
import { useCallback, useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import LogoIcon from '@assets/logo.svg'

export default function NavBar() {
  const t = useTranslations('component.NavBar')
  const [user, setUser] = useState<UserModel | null>()

  const getMe = useCallback(async () => {
    const me = await action.userGetMe()

    if (!me?.user) {
      setUser(null)
    } else {
      setUser(me.user)
    }
  }, [])

  useEffect(() => {
    getMe()
  }, [getMe])

  return (
    <div className='flex justify-center px-5'>
      <nav className='navbar container px-0'>
        <div className='navbar-start'>
          <div className='dropdown'>
            <div
              tabIndex={0}
              role='button'
              className='btn btn-circle btn-primary'
            >
              <Bars3Icon className='h-5 w-5' />
            </div>
            <ul tabIndex={0} className='menu menu-sm dropdown-content w-52'>
              <li>
                <Link href='/' aria-label={t('home.aria-label')}>
                  {t('home.title')}
                </Link>
              </li>
              <li>
                <Link
                  href='/skirm-finder'
                  aria-label={t('skirm-finder.aria-label')}
                >
                  {t('skirm-finder.title')}
                </Link>
              </li>
              <li>
                <Link
                  href='/organizations'
                  aria-label={t('organizations.aria-label')}
                >
                  {t('organizations.title')}
                </Link>
              </li>
              <li>
                <Link href='/users' aria-label={t('users.aria-label')}>
                  {t('users.title')}
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className='navbar-center -mb-2 sm:-mb-6'>
          <Link
            href='/'
            aria-label={t('logo.aria-label')}
            className='text-primary'
          >
            <LogoIcon className='size-12 sm:size-16' />
          </Link>
        </div>
        <div className='navbar-end'>
          {user === undefined ? (
            <button
              className='btn btn-primary'
              aria-label='User action menu is loading'
              disabled
            >
              <span className='loading loading-spinner'></span>
            </button>
          ) : user === null ? (
            <Link
              className='btn btn-primary'
              href='/sign-in'
              aria-label={t('sign-in.aria-label')}
            >
              {t('sign-in.title')}
            </Link>
          ) : (
            <NavBarUserControlPanel user={user} />
          )}
        </div>
      </nav>
    </div>
  )
}

type NavBarUserControlPanelProps = {
  user: UserModel
}

function NavBarUserControlPanel({ user }: NavBarUserControlPanelProps) {
  const t = useTranslations('component.NavBar.NavBarUserControlPanel')
  return (
    <div className='dropdown dropdown-end'>
      <div className='indicator'>
        {user.isSubscriber && (
          <span className='indicator-item badge badge-warning badge-xs right-2.5'>
            {t('Pro.title')}
          </span>
        )}
        <div tabIndex={0} role='button' className='btn btn-primary btn-circle'>
          <p>{user.username[0]?.toLocaleUpperCase()}</p>
        </div>
      </div>
      <ul tabIndex={0} className='menu menu-sm dropdown-content w-52'>
        {!user.isSubscriber && (
          <li>
            <Link
              className='text-warning'
              href={`/users/${user.id}/become-pro`}
              aria-label={t('become-pro.aria-label')}
            >
              {t('become-pro.title')}
            </Link>
          </li>
        )}
        <li>
          <Link
            href={`/users/${user.id}/settings`}
            aria-label={t('user-settings.aria-label')}
          >
            {t('user-settings.title')}
          </Link>
        </li>
        <li>
          <Link
            href={`/users/${user.id}/manage-skirms`}
            aria-label={t('skirms.aria-label')}
          >
            {t('skirms.title')}
          </Link>
        </li>
        <li>
          <Link
            href={`/users/${user.id}/manage-organizations`}
            aria-label={t('organizations.aria-label')}
          >
            {t('organizations.title')}
          </Link>
        </li>
        <li>
          <form action={authSignOut} className='flex'>
            <button
              className='text-error grow text-start'
              type='submit'
              aria-label={t('sign-out.aria-label')}
            >
              {t('sign-out.title')}
            </button>
          </form>
        </li>
      </ul>
    </div>
  )
}
